/**
 * Settings → Import (owners and admins; contracts imports.ts, rules in logic/imports): bring a Slack workspace's
 * conversations or a Notion workspace's pages in. Start: pick the source, then upload its export .zip (the regular
 * upload queue, `purpose: "import"`) or paste a token. The server scans it; this view model polls the job every
 * POLL_MS while it scans or imports. Review: Slack — match people to members, pick and rename channels, include DMs;
 * Notion — the proposed sections (rename, private/shared, move pages between them) and database views. Then start,
 * watch the progress, and see what came over. Online only.
 */
import type { ImportJob, ImportSource, ImportTrashCounts, ImportVia, Member, NotionImportPlan, PageVisibility, SlackAppSettings, SlackPersonChoice, SlackSyncSettings, ViewLayout } from "@bundle/contracts";
import { msg, type MessageDescriptor, type MessageKey } from "../i18n.ts";
import { uploadProgress } from "../logic/files.ts";
import {
  type SlackReview,
  addSection,
  inviteAllMissing,
  isRunning,
  setRestrictExternal,
  moveNode,
  notionProblems,
  notionStartRequest,
  removeSection,
  renameSection,
  setDatabaseLayout,
  setIncluded,
  setSectionVisibility,
  slackDefaults,
  slackProblems,
  slackStartRequest,
} from "../logic/imports.ts";
import { isRestricted } from "../logic/access.ts";
import { isManager } from "../logic/roles.ts";
import { ApiFailure, errorKind, type PickedFile } from "../ports.ts";
import type { SettingsDeps } from "./SettingsSupport.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

const POLL_MS = 2000;
/** While a Slack sync run goes (after "Sync now"), its status is read again this often. */
const SYNC_POLL_MS = 5000;

export type ImportUpload = { name: string; progress: number };

export type ImportSettingsState = {
  status: "loading" | "ready" | "error";
  /** Owners and admins only (others see a note). */
  manager: boolean;
  /** Settings → Data: my own Slack import (docs/features/imports.md) instead of the workspace's imports. */
  personal: boolean;
  /** Personal mode: I may import my own conversations (an active person, not a guest). */
  canImportOwn: boolean;
  /** The Slack app people authorise for their own imports (admins also see its Client ID). */
  slackApp?: SlackAppSettings;
  /** Recent imports, newest first. */
  jobs: ImportJob[];
  /** The job on screen (with its preview once it's in review). Absent: the start form. */
  current?: ImportJob;
  /** Start form. */
  source: ImportSource;
  via: ImportVia;
  token: string;
  /** Slack token: only fetch again the files messages still link to on Slack (no new import). */
  repairLinks: boolean;
  /** The export uploading now. */
  upload?: ImportUpload;
  busy: boolean;
  /** Review edits. */
  slack?: SlackReview;
  notion?: NotionImportPlan;
  /** Members to match Slack people to (active people, by name). */
  members: Member[];
  notice?: MessageDescriptor;
  /**
   * "Move to trash" / "Delete permanently" on the ended job: the confirmation, with what it would touch (counted by the
   * server). The run itself shows on the job (`undo`).
   */
  trash?: { jobId: string; action: "trash" | "delete"; counts: ImportTrashCounts; busy: boolean };
  /** The hourly Slack → Bundle sync of a finished Slack import (owners and admins; absent until read). */
  sync?: SlackSyncSettings;
  /** Turning the sync on needs a Slack token (none kept): the field shows. */
  syncNeedsToken: boolean;
  syncToken: string;
  syncBusy: boolean;
};

export class ImportSettingsViewModel extends ViewModel<ImportSettingsState> {
  private stopPoll: (() => void) | undefined;
  private stopSyncPoll: (() => void) | undefined;
  private generation = 0;

  constructor(
    readonly ws: WorkspaceViewModel,
    private readonly deps: SettingsDeps,
    opts: { personal?: boolean } = {},
  ) {
    super({
      status: "loading", manager: false, personal: opts.personal === true, canImportOwn: false, jobs: [], source: "slack", via: "token", token: "",
      repairLinks: false, busy: false, members: [], syncNeedsToken: false, syncToken: "", syncBusy: false,
    });
    if (!opts.personal) this.state.via = "file";
    this.onDispose(ws.subscribe(() => this.follow()));
    this.onDispose(() => this.stopPoll?.());
    this.onDispose(() => this.stopSyncPoll?.());
    this.follow();
  }

  private follow(): void {
    const cache = this.ws.state.cache;
    const me = cache?.members[this.ws.state.meId];
    const manager = me !== undefined && isManager(me, this.ws.deps.clock.now());
    const canImportOwn = me !== undefined && me.kind === "human" && !me.deactivated && !isRestricted(me);
    if (canImportOwn !== this.state.canImportOwn) this.update({ canImportOwn });
    const members = cache
      ? Object.values(cache.members)
          .filter((m) => m.kind === "human" && !m.deactivated)
          .toSorted((a, b) => a.displayName.localeCompare(b.displayName))
      : [];
    if (manager !== this.state.manager || members.length !== this.state.members.length) this.update({ manager, members });
  }

  async load(): Promise<void> {
    try {
      const jobs = this.state.personal ? await this.deps.api.myImports(this.ws.wsId) : await this.deps.api.imports(this.ws.wsId);
      if (this.isDisposed) return;
      this.update({ status: "ready", jobs });
      void this.loadSlackApp();
      if (!this.state.personal) void this.loadSync();
      // Reopen what is still going (or waiting for review). Google Drive jobs have their own screen (DriveImportViewModel).
      const open = jobs.find((j) => j.source !== "googleDrive" && (isRunning(j.status) || j.status === "review"));
      if (open && this.state.current === undefined) await this.open(open.id);
    } catch {
      if (!this.isDisposed && this.state.status !== "ready") this.update({ status: "error" });
    }
  }

  // ---------- Start form ----------

  setSource(source: ImportSource): void {
    this.update({ source });
  }

  setVia(via: ImportVia): void {
    this.update({ via });
  }

  setToken(token: string): void {
    this.update({ token });
  }

  setRepairLinks(repairLinks: boolean): void {
    this.update({ repairLinks });
  }

  /** The picked export: uploads it, then starts scanning. */
  async pickFile(file: PickedFile): Promise<void> {
    if (this.state.busy) return;
    if (!file.name.toLowerCase().endsWith(".zip")) {
      this.update({ notice: msg("import.notZip") });
      return;
    }
    const gen = ++this.generation;
    this.update({ busy: true, upload: { name: file.name, progress: 0 }, notice: undefined });
    const data = this.ws.deps.data;
    const handle = data.uploadFile(this.ws.wsId, { ...file, mime: file.mime || "application/zip" }, { type: "import" });
    const uploads = data.uploads(this.ws.wsId);
    const unsubscribe = uploads.subscribe(() => {
      const entry = uploads.getSnapshot().find((u) => u.id === handle.id);
      if (entry && gen === this.generation && this.state.upload) this.update({ upload: { name: file.name, progress: uploadProgress(entry.state) } });
    });
    try {
      const stored = await handle.done;
      unsubscribe();
      if (gen !== this.generation || this.isDisposed) return;
      await this.create({ source: this.state.source, fileId: stored.id });
    } catch (e) {
      unsubscribe();
      if (gen === this.generation && !this.isDisposed) this.update({ busy: false, upload: undefined, notice: this.failure(e, "import.uploadFailed") });
    }
  }

  async submitToken(): Promise<void> {
    const token = this.state.token.trim();
    if (this.state.busy || token.length < 10) return;
    this.update({ busy: true, notice: undefined });
    await this.create(this.state.source === "slack" && this.state.repairLinks ? { source: "slack", token, repairLinks: true } : { source: this.state.source, token });
  }

  private async create(req: { source: ImportSource; fileId?: string; token?: string; repairLinks?: boolean }): Promise<void> {
    try {
      const job = await this.deps.api.createImport(this.ws.wsId, req);
      if (this.isDisposed) return;
      this.update({ busy: false, upload: undefined, token: "", repairLinks: false, current: job, jobs: [job, ...this.state.jobs.filter((j) => j.id !== job.id)] });
      this.poll();
    } catch (e) {
      if (!this.isDisposed) this.update({ busy: false, upload: undefined, notice: this.failure(e, "import.startFailed") });
    }
  }

  /** Cancels the upload in progress (before the job exists). */
  cancelUpload(): void {
    this.generation += 1;
    this.update({ busy: false, upload: undefined });
  }

  // ---------- The job ----------

  async open(importId: string): Promise<void> {
    try {
      const job = await this.deps.api.importJob(this.ws.wsId, importId);
      if (this.isDisposed) return;
      this.adopt(job);
    } catch (e) {
      if (!this.isDisposed) this.update({ notice: this.failure(e, "import.loadFailed") });
    }
  }

  /** Back to the start form and the list. */
  close(): void {
    this.stopPoll?.();
    this.stopPoll = undefined;
    this.update({ current: undefined, slack: undefined, notion: undefined });
    void this.load();
  }

  private adopt(job: ImportJob): void {
    const prev = this.state.current;
    const patch: Partial<ImportSettingsState> = { current: job, jobs: this.state.jobs.some((j) => j.id === job.id) ? this.state.jobs.map((j) => (j.id === job.id ? job : j)) : [job, ...this.state.jobs] };
    // The review starts from the server's proposal once.
    if (job.status === "review" && (prev?.id !== job.id || prev.status !== "review")) {
      if (job.slack) patch.slack = slackDefaults(job.slack, { personal: job.personal === true });
      if (job.notion) patch.notion = job.notion.plan;
    }
    this.update(patch);
    if (isRunning(job.status) || job.undo?.status === "running") this.poll();
    else {
      this.stopPoll?.();
      this.stopPoll = undefined;
    }
  }

  private poll(): void {
    this.stopPoll?.();
    this.stopPoll = this.ws.deps.clock.setTimer(POLL_MS, () => {
      this.stopPoll = undefined;
      const id = this.state.current?.id;
      if (id !== undefined && !this.isDisposed) void this.open(id);
    });
  }

  // ---------- Review: Slack ----------

  /** What happens to one person (a member, invite, external guest, deactivated profile or name only). */
  setPerson(slackId: string, choice: SlackPersonChoice): void {
    const r = this.state.slack;
    if (r) this.update({ slack: { ...r, people: { ...r.people, [slackId]: choice } } });
  }

  /** Everyone not matched to a member is invited (external people as guests); who left stays deactivated. */
  inviteAllMissing(): void {
    const r = this.state.slack;
    const p = this.state.current?.slack;
    if (r && p) this.update({ slack: inviteAllMissing(p, r) });
  }

  includeChannel(slackId: string, include: boolean): void {
    const r = this.state.slack;
    const c = r?.channels[slackId];
    if (r && c) this.update({ slack: { ...r, channels: { ...r.channels, [slackId]: { ...c, include } } } });
  }

  /** Every DM and group DM with people at once — or, with `apps`, every DM with an app. */
  includeDms(include: boolean, apps = false): void {
    const r = this.state.slack;
    const p = this.state.current?.slack;
    if (!r || !p) return;
    const channels = { ...r.channels };
    for (const c of p.channels) if ((c.kind === "dm" || c.kind === "group_dm") && (c.app !== undefined) === apps) channels[c.slackId] = { ...channels[c.slackId]!, include };
    this.update({ slack: { ...r, channels } });
  }

  renameChannel(slackId: string, name: string): void {
    const r = this.state.slack;
    const c = r?.channels[slackId];
    if (r && c) this.update({ slack: { ...r, channels: { ...r.channels, [slackId]: { ...c, name } } } });
  }

  /** The rule for people from another company, for the whole import. */
  setRestrictExternal(on: boolean): void {
    const r = this.state.slack;
    const p = this.state.current?.slack;
    if (r && p) this.update({ slack: setRestrictExternal(p, r, on) });
  }

  setSendInvites(sendInvites: boolean): void {
    const r = this.state.slack;
    if (r) this.update({ slack: { ...r, sendInvites } });
  }

  setCopyFiles(files: boolean): void {
    const r = this.state.slack;
    if (r) this.update({ slack: { ...r, files } });
  }

  // ---------- Review: Notion ----------

  /** Picks or leaves out top-level pages / databases (the review's checkboxes; all of them for "Everything"). */
  includeNodes(keys: string[], include: boolean): void {
    if (this.state.notion) this.update({ notion: setIncluded(this.state.notion, keys, include) });
  }

  moveNode(key: string, sectionId: string | null): void {
    if (this.state.notion) this.update({ notion: moveNode(this.state.notion, key, sectionId) });
  }

  renameSection(id: string, title: string): void {
    if (this.state.notion) this.update({ notion: renameSection(this.state.notion, id, title) });
  }

  setSectionVisibility(id: string, visibility: PageVisibility): void {
    if (this.state.notion) this.update({ notion: setSectionVisibility(this.state.notion, id, visibility) });
  }

  /** A new section at the end; `moveKey`: a top-level page / database to put in it right away. */
  addSection(title: string, moveKey?: string): void {
    if (!this.state.notion) return;
    const id = this.ws.deps.ids.next();
    const plan = addSection(this.state.notion, id, title);
    this.update({ notion: moveKey === undefined ? plan : moveNode(plan, moveKey, id) });
  }

  removeSection(id: string): void {
    if (this.state.notion) this.update({ notion: removeSection(this.state.notion, id) });
  }

  setDatabaseLayout(key: string, layout: ViewLayout): void {
    if (this.state.notion) this.update({ notion: setDatabaseLayout(this.state.notion, key, layout) });
  }

  /** Nothing blocks starting. */
  canStart(): boolean {
    const job = this.state.current;
    if (job?.status !== "review" || this.state.busy) return false;
    if (job.slack && this.state.slack) {
      const p = slackProblems(job.slack, this.state.slack);
      return p.duplicateNames.length === 0 && p.emptyNames.length === 0;
    }
    if (this.state.notion) {
      const p = notionProblems(this.state.notion, job.notion?.nodes.filter((n) => n.parentKey === null).map((n) => n.key));
      return p.untitled.length === 0 && !p.nothingPicked;
    }
    return false;
  }

  async start(): Promise<void> {
    const job = this.state.current;
    if (job === undefined || !this.canStart()) return;
    const req = job.slack && this.state.slack ? slackStartRequest(job.slack, this.state.slack) : notionStartRequest(this.state.notion!);
    this.update({ busy: true, notice: undefined });
    try {
      const next = await this.deps.api.startImport(this.ws.wsId, job.id, req);
      if (this.isDisposed) return;
      this.update({ busy: false });
      this.adopt(next);
    } catch (e) {
      if (!this.isDisposed) this.update({ busy: false, notice: this.failure(e, "import.startFailed") });
    }
  }

  /** Reads the source again (review only); the review restarts from the fresh suggestions. */
  async rescan(): Promise<void> {
    const job = this.state.current;
    if (job?.status !== "review" || this.state.busy) return;
    this.update({ busy: true, notice: undefined });
    try {
      const next = await this.deps.api.rescanImport(this.ws.wsId, job.id);
      if (this.isDisposed) return;
      // Force the review to start over from the new reading.
      this.update({ busy: false, current: { ...job, status: "scanning" } });
      this.adopt(next);
    } catch (e) {
      if (!this.isDisposed) this.update({ busy: false, notice: this.failure(e, "import.rescanFailed") });
    }
  }

  async cancel(): Promise<void> {
    const job = this.state.current;
    if (job === undefined) return;
    this.update({ busy: true });
    try {
      const next = await this.deps.api.cancelImport(this.ws.wsId, job.id);
      if (this.isDisposed) return;
      this.update({ busy: false });
      this.adopt(next);
    } catch (e) {
      if (!this.isDisposed) this.update({ busy: false, notice: this.failure(e, "import.cancelFailed") });
    }
  }

  // ---------- Undo an ended Notion import: Move to trash, then Delete permanently (Notion's rule) ----------

  /** The job on screen can be undone: a Notion import that ended (done, failed or cancelled), no undo running. */
  canUndo(): boolean {
    const job = this.state.current;
    return job !== undefined && job.source === "notion" && (job.status === "done" || job.status === "failed" || job.status === "cancelled") && job.undo?.status !== "running";
  }

  /** Counts what would go to the trash, then asks (nothing to move: says so instead). */
  askTrash(): Promise<void> {
    return this.ask("trash");
  }

  /** Counts the job's items in the trash, then asks (strong confirmation: it can't be undone). */
  askDelete(): Promise<void> {
    return this.ask("delete");
  }

  private async ask(action: "trash" | "delete"): Promise<void> {
    const job = this.state.current;
    if (job === undefined || !this.canUndo() || this.state.busy) return;
    this.update({ busy: true, notice: undefined });
    try {
      const counts = action === "trash" ? await this.deps.api.importTrash(this.ws.wsId, job.id) : await this.deps.api.importDeletable(this.ws.wsId, job.id);
      if (this.isDisposed) return;
      if (counts.pages + counts.databases === 0) this.update({ busy: false, notice: msg(action === "trash" ? "import.trash.nothing" : "import.delete.nothing") });
      else this.update({ busy: false, trash: { jobId: job.id, action, counts, busy: false } });
    } catch (e) {
      if (!this.isDisposed) this.update({ busy: false, notice: this.failure(e, action === "trash" ? "import.trash.failed" : "import.delete.failed") });
    }
  }

  cancelTrash(): void {
    if (this.state.trash && !this.state.trash.busy) this.update({ trash: undefined });
  }

  /** Starts the run (a server Workflow); the job card follows its progress. */
  async confirmTrash(): Promise<void> {
    const t = this.state.trash;
    if (t === undefined || t.busy) return;
    this.update({ trash: { ...t, busy: true } });
    try {
      const undo = t.action === "trash" ? await this.deps.api.trashImport(this.ws.wsId, t.jobId) : await this.deps.api.deleteImport(this.ws.wsId, t.jobId);
      if (this.isDisposed) return;
      this.update({ trash: undefined });
      const job = this.state.current;
      if (job?.id === t.jobId) this.adopt({ ...job, undo });
    } catch (e) {
      if (!this.isDisposed) this.update({ trash: undefined, notice: this.failure(e, t.action === "trash" ? "import.trash.failed" : "import.delete.failed") });
    }
  }

  dismissNotice(): void {
    this.update({ notice: undefined });
  }

  // ---------- The hourly Slack sync ----------

  async loadSync(): Promise<void> {
    try {
      const sync = await this.deps.api.slackSync(this.ws.wsId);
      if (this.isDisposed) return;
      this.update({ sync });
      this.followSync(sync);
    } catch {
      // Settings without it: the section stays hidden.
    }
  }

  setSyncToken(syncToken: string): void {
    this.update({ syncToken });
  }

  /** "Keep syncing from Slack (hourly)": on (a token first when none is kept) or paused. */
  async setSyncEnabled(enabled: boolean): Promise<void> {
    const sync = this.state.sync;
    if (!sync || this.state.syncBusy) return;
    if (enabled && !sync.hasToken && this.state.syncToken.trim().length < 10) {
      this.update({ syncNeedsToken: true });
      return;
    }
    if (!enabled && this.state.syncNeedsToken) {
      this.update({ syncNeedsToken: false, syncToken: "" });
      if (!sync.enabled) return;
    }
    await this.saveSync(enabled);
  }

  /** The token field's "Turn on". */
  async submitSyncToken(): Promise<void> {
    if (this.state.syncToken.trim().length < 10) return;
    await this.saveSync(true);
  }

  private async saveSync(enabled: boolean): Promise<void> {
    const token = this.state.syncToken.trim();
    this.update({ syncBusy: true, notice: undefined });
    try {
      const sync = await this.deps.api.updateSlackSync(this.ws.wsId, token.length >= 10 ? { enabled, token } : { enabled });
      if (!this.isDisposed) this.update({ sync, syncBusy: false, syncToken: "", syncNeedsToken: false });
    } catch (e) {
      if (this.isDisposed) return;
      if (e instanceof ApiFailure && e.code === "needs_token") this.update({ syncBusy: false, syncNeedsToken: true });
      else this.update({ syncBusy: false, notice: e instanceof ApiFailure && e.status === 400 ? msg("import.sync.badToken") : this.failure(e, "import.sync.failed") });
    }
  }

  /** "Sync now": a run starts (or the one going on is kept); its status is followed until it ends. */
  async syncNow(): Promise<void> {
    if (!this.state.sync || this.state.syncBusy) return;
    this.update({ syncBusy: true, notice: undefined });
    try {
      const sync = await this.deps.api.runSlackSync(this.ws.wsId);
      if (this.isDisposed) return;
      this.update({ sync, syncBusy: false });
      this.followSync(sync);
    } catch (e) {
      if (this.isDisposed) return;
      if (e instanceof ApiFailure && e.code === "needs_token") this.update({ syncBusy: false, syncNeedsToken: true });
      else this.update({ syncBusy: false, notice: this.failure(e, "import.sync.failed") });
    }
  }

  /** "Stop syncing (Finish move)": the sync stops for good and the token is forgotten. */
  async finishMove(): Promise<void> {
    if (!this.state.sync || this.state.syncBusy) return;
    this.update({ syncBusy: true, notice: undefined });
    try {
      const sync = await this.deps.api.stopSlackSync(this.ws.wsId);
      if (this.isDisposed) return;
      this.stopSyncPoll?.();
      this.stopSyncPoll = undefined;
      this.update({ sync, syncBusy: false, syncNeedsToken: false, syncToken: "", notice: msg("import.sync.finished") });
    } catch (e) {
      if (!this.isDisposed) this.update({ syncBusy: false, notice: this.failure(e, "import.sync.failed") });
    }
  }

  private followSync(sync: SlackSyncSettings): void {
    this.stopSyncPoll?.();
    this.stopSyncPoll = undefined;
    if (sync.status !== "running") return;
    this.stopSyncPoll = this.ws.deps.clock.setTimer(SYNC_POLL_MS, () => {
      this.stopSyncPoll = undefined;
      if (!this.isDisposed) void this.loadSync();
    });
  }

  // ---------- My own Slack conversations ----------

  private async loadSlackApp(): Promise<void> {
    try {
      const slackApp = await this.deps.api.slackApp(this.ws.wsId);
      if (!this.isDisposed) this.update({ slackApp });
    } catch {
      // Without it the personal card says to ask an admin; nothing else depends on it.
    }
  }

  /** Owners and admins: the Slack app people authorise (its secret is sealed on the server and never returned). */
  async saveSlackApp(clientId: string, clientSecret: string): Promise<boolean> {
    if (this.state.busy || clientId.trim().length < 5 || clientSecret.trim().length < 10) return false;
    this.update({ busy: true, notice: undefined });
    try {
      const slackApp = await this.deps.api.updateSlackApp(this.ws.wsId, { clientId: clientId.trim(), clientSecret: clientSecret.trim() });
      if (!this.isDisposed) this.update({ busy: false, slackApp, notice: msg("import.slackApp.saved") });
      return true;
    } catch (e) {
      if (!this.isDisposed) this.update({ busy: false, notice: this.failure(e, "import.slackApp.failed") });
      return false;
    }
  }

  /** My own import: Slack's OAuth URL to send me to (Slack brings me back here with the new import). */
  async connectSlack(): Promise<string | undefined> {
    if (this.state.busy) return undefined;
    this.update({ busy: true, notice: undefined });
    try {
      const url = await this.deps.api.connectSlack(this.ws.wsId);
      if (!this.isDisposed) this.update({ busy: false });
      return url;
    } catch (e) {
      if (!this.isDisposed) this.update({ busy: false, notice: this.failure(e, "import.personal.connectFailed") });
      return undefined;
    }
  }

  /** Slack brought me back without an import (I declined, the link expired…). */
  showConnectError(): void {
    this.update({ notice: msg("import.personal.connectFailed") });
  }

  private failure(e: unknown, fallback: MessageKey): MessageDescriptor {
    if (errorKind(e) === "network") return msg("settings.offline");
    if (e instanceof ApiFailure && e.code === "busy") return msg("import.busy");
    if (e instanceof ApiFailure && e.code === "not_configured") return msg("import.personal.notConfigured");
    return msg(fallback);
  }
}
